/**
 * ScenarioConfigPanel.jsx — read-only summary of the ACTIVE scenario's configuration
 * (escalation, tax, other %, rate overrides, added charges, source) plus the
 * usage-weighted baseline $/unit rate the budget is actually computed at.
 *
 * This makes AI / market / contract assumptions visible and auditable — the user
 * can see exactly what rates & settings drive the budget, then click Edit to change.
 */
import React, { useState } from 'react';
import { Pencil, ChevronDown, ChevronRight, Sparkles, FileSignature, LineChart, SlidersHorizontal } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const SOURCE_META = {
  manual: { label: 'Manual', icon: SlidersHorizontal, cls: 'bg-slate-100 text-slate-700' },
  ai: { label: 'AI (Beacon)', icon: Sparkles, cls: 'bg-violet-100 text-violet-700' },
  contract: { label: 'Contracted rates', icon: FileSignature, cls: 'bg-emerald-100 text-emerald-700' },
  market: { label: 'Market rates', icon: LineChart, cls: 'bg-amber-100 text-amber-700' },
  'market-seasonal': { label: 'Market (seasonal)', icon: LineChart, cls: 'bg-amber-100 text-amber-700' },
  csv: { label: 'CSV upload', icon: SlidersHorizontal, cls: 'bg-sky-100 text-sky-700' },
};

const pct = (v) => (v == null || v === '' ? null : `${v}%`);
const rate = (v) => (v == null ? '—' : `$${Number(v).toFixed(4)}`);

function Item({ label, value }) {
  if (value == null) return null;
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}

export function ScenarioConfigPanel({ scenario, rateBasis, onEdit }) {
  const [open, setOpen] = useState(false);
  if (!scenario) return null;
  const a = scenario.assumptions || {};
  const src = a.source || 'manual';
  const meta = SOURCE_META[src] || SOURCE_META.manual;
  const Icon = meta.icon;
  const esc = a.escalation || {};
  const overrides = a.rate_overrides || [];
  const charges = a.added_charges || [];
  const wc = a.water_components || {};
  const commodityRates = (rateBasis?.commodity_rates) || {};

  return (
    <div className="border rounded-lg bg-card" data-testid="scenario-config-panel">
      <div className="w-full flex items-center justify-between gap-2 p-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 min-w-0 text-left flex-1"
          data-testid="scenario-config-toggle"
        >
          {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
          <span className="text-sm font-semibold truncate">Scenario configuration</span>
          <Badge className={`${meta.cls} border-0 gap-1`} data-testid="scenario-config-source">
            <Icon className="w-3 h-3" /> {meta.label}
          </Badge>
          {!open && (
            <span className="text-xs text-muted-foreground hidden sm:inline truncate">
              {overrides.length > 0 ? `${overrides.length} rate override${overrides.length > 1 ? 's' : ''}` : 'Historical rates'}
              {esc.supply != null && ` · supply +${esc.supply}%`}
            </span>
          )}
        </button>
        <Button size="sm" variant="outline" onClick={() => onEdit?.(scenario)}
          data-testid="scenario-config-edit">
          <Pencil className="w-3.5 h-3.5 mr-1" /> Edit settings
        </Button>
      </div>

      {open && (
        <div className="px-3 pb-3 space-y-4 border-t pt-3" data-testid="scenario-config-body">
          {/* The rate the budget is computed at */}
          {Object.keys(commodityRates).length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-1.5">Calculated at these baseline rates (year 1, usage-weighted)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(commodityRates).map(([comm, r]) => (
                  <div key={comm} className="rounded-md border bg-muted/30 px-3 py-2 text-xs" data-testid={`config-rate-${comm}`}>
                    <span className="capitalize font-medium">{comm}</span>
                    <span className="text-muted-foreground"> — supply </span>
                    <span className="font-mono">{rate(r.supply_per_unit)}/{r.uom}</span>
                    <span className="text-muted-foreground"> · delivery </span>
                    <span className="font-mono">{rate(r.delivery_per_unit)}/{r.uom}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Item label="Supply escalation" value={pct(esc.supply) || 'default'} />
            <Item label="Delivery escalation" value={pct(esc.delivery) || 'default'} />
            <Item label="Tax override" value={pct(a.tax_pct) || 'from history'} />
            <Item label="Other charges / yr" value={pct(a.other_charges_pct) || 'from history'} />
          </div>

          {/* Water/sewer component escalations */}
          {Object.keys(wc).length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-1.5">Water / sewer component escalation</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                {Object.entries(wc).map(([k, v]) => (
                  <div key={k} className="rounded border px-2 py-1">
                    <span className="capitalize">{k.replace(/_/g, ' ')}</span>: <span className="font-medium">{v}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rate overrides */}
          {overrides.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-1.5">Rate overrides ({overrides.length})</div>
              <div className="max-h-40 overflow-y-auto rounded border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1 font-medium">Commodity</th>
                      <th className="text-left px-2 py-1 font-medium">Period</th>
                      <th className="text-right px-2 py-1 font-medium">Supply $/unit</th>
                      <th className="text-right px-2 py-1 font-medium">Delivery $/unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.slice(0, 60).map((o, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 capitalize">{o.commodity || 'all'}</td>
                        <td className="px-2 py-1">{o.period_from || '—'} → {o.period_to || '—'}</td>
                        <td className="px-2 py-1 text-right font-mono">{o.supply_rate != null ? rate(o.supply_rate) : '—'}</td>
                        <td className="px-2 py-1 text-right font-mono">{o.delivery_rate != null ? rate(o.delivery_rate) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Added charges */}
          {charges.length > 0 && (
            <div>
              <div className="text-xs font-semibold mb-1.5">Added charges</div>
              <div className="flex flex-wrap gap-1.5">
                {charges.map((c, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {c.label}: {c.kind === 'pct' ? `${c.value}%` : `$${c.value}`} on {c.applies_to}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {a.notes && <p className="text-xs text-muted-foreground italic">{a.notes}</p>}
        </div>
      )}
    </div>
  );
}
